// src/components/SonoShare.js
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { SCAN_ENUMS } from './ReportWriter';

// ─── Viewport hook ───
const useViewport = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    width,
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isDesktop: width >= 1024,
  };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const compressImage = (file, maxSizeMB = 1) =>
  new Promise((resolve, reject) => {
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size <= maxBytes) {
      resolve(file);
      return;
    }
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      const maxDim = 1200;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      let quality = 0.8;
      const attempt = () =>
        canvas.toBlob(
          (blob) => {
            if (blob.size <= maxBytes || quality <= 0.1) {
              resolve(
                new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                })
              );
            } else {
              quality -= 0.15;
              attempt();
            }
          },
          'image/jpeg',
          quality
        );
      attempt();
    };
    img.onerror = reject;
  });

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const statusColor = {
  PENDING_REVIEW: '#e67e22',
  RESOLVED: '#27ae60',
  SHARED: '#3498db',
};
const statusLabel = {
  PENDING_REVIEW: 'Pending Review',
  RESOLVED: 'Resolved',
  SHARED: 'Shared',
};

// ─── Media Editor ─────────────────────────────────────────────────────────────

const MediaEditor = ({ file, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [imageObj, setImageObj] = useState(null);
  const [activeTab, setActiveTab] = useState('redact');
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [redactMode, setRedactMode] = useState('blackout');
  const [rects, setRects] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (file.type.startsWith('video')) return;
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => setImageObj(img);
  }, [file]);

  useEffect(() => {
    if (!imageObj || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const isVertical = rotation % 180 !== 0;
    const ar = imageObj.height / imageObj.width;
    const base = 600;
    canvas.width = isVertical ? base * ar : base;
    canvas.height = isVertical ? base : base * ar;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
    let dw = base,
      dh = base * ar;
    if (isVertical) {
      const s = canvas.height / imageObj.width;
      dw = imageObj.width * s;
      dh = imageObj.height * s;
    }
    ctx.drawImage(imageObj, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
    rects.forEach((r) => {
      if (r.type === 'blur') {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.font = 'bold 11px sans-serif';
        ctx.fillStyle = '#888';
        ctx.fillText('BLURRED', r.x + 4, r.y + 14);
      } else {
        ctx.fillStyle = '#000';
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
    });
  }, [imageObj, brightness, contrast, rotation, rects]);

  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) * canvasRef.current.width) / r.width,
      y: ((e.clientY - r.top) * canvasRef.current.height) / r.height,
    };
  };
  const startDraw = (e) => {
    if (activeTab !== 'redact') return;
    setIsDrawing(true);
    setStartPos(getPos(e));
  };
  const drawMove = (e) => {
    if (!isDrawing || activeTab !== 'redact') return;
    const p = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle =
      redactMode === 'blackout'
        ? 'rgba(0,0,0,0.45)'
        : 'rgba(200,200,200,0.45)';
    ctx.fillRect(startPos.x, startPos.y, p.x - startPos.x, p.y - startPos.y);
  };
  const endDraw = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const p = getPos(e);
    if (Math.abs(p.x - startPos.x) < 5 || Math.abs(p.y - startPos.y) < 5)
      return;
    setRects((prev) => [
      ...prev,
      {
        x: startPos.x,
        y: startPos.y,
        w: p.x - startPos.x,
        h: p.y - startPos.y,
        type: redactMode,
      },
    ]);
  };
  const handleSave = () => {
    if (file.type.startsWith('video')) {
      onSave(file);
      return;
    }
    canvasRef.current.toBlob(
      (blob) =>
        onSave(
          new File([blob], 'edited_' + file.name, { type: 'image/jpeg' })
        ),
      'image/jpeg',
      0.85
    );
  };

  if (file.type.startsWith('video'))
    return (
      <div style={overlay}>
        <div style={editorBox}>
          <h3 style={{ color: '#e74c3c', margin: '0 0 15px' }}>
            Video Verification
          </h3>
          <video
            src={URL.createObjectURL(file)}
            controls
            style={{
              maxHeight: 340,
              maxWidth: '100%',
              borderRadius: 8,
              marginBottom: 15,
            }}
          />
          <p
            style={{
              color: '#bbb',
              fontSize: '0.85rem',
              marginBottom: 20,
            }}
          >
            Ensure all patient identifiers are removed before uploading.
          </p>
          <div
            style={{ display: 'flex', gap: 10, justifyContent: 'center' }}
          >
            <button className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleSave}>
              Confirm &amp; Use
            </button>
          </div>
        </div>
      </div>
    );

  const tabBtn = (id, label) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        flex: 1,
        padding: '9px 0',
        background: activeTab === id ? '#3498db' : 'transparent',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '0.82rem',
        fontWeight: activeTab === id ? 600 : 400,
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#1a1a2e',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 52,
          background: '#16213e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          borderBottom: '1px solid #0f3460',
        }}
      >
        <span
          style={{ color: '#3498db', fontWeight: 700, fontSize: '1rem' }}
        >
          🎨 Studio Editor
        </span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => {
              setRects([]);
              setBrightness(100);
              setContrast(100);
              setRotation(0);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#e74c3c',
              cursor: 'pointer',
              fontSize: '0.82rem',
            }}
          >
            ↺ Reset
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            style={{ padding: '6px 18px', fontSize: '0.85rem' }}
          >
            Save &amp; Use ✓
          </button>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '1.4rem',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#0d0d1a',
          padding: 20,
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={startDraw}
          onMouseMove={drawMove}
          onMouseUp={endDraw}
          style={{
            boxShadow: '0 0 30px rgba(52,152,219,0.2)',
            cursor: activeTab === 'redact' ? 'crosshair' : 'default',
            maxWidth: '100%',
            maxHeight: '100%',
            borderRadius: 4,
          }}
        />
      </div>
      <div
        style={{
          height: 130,
          background: '#16213e',
          borderTop: '1px solid #0f3460',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', borderBottom: '1px solid #0f3460' }}>
          {tabBtn('redact', '⬛ Redact / Blur')}
          {tabBtn('adjust', '☀️ Adjust')}
          {tabBtn('rotate', '↻ Rotate')}
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
            padding: '0 20px',
          }}
        >
          {activeTab === 'redact' && (
            <>
              <button
                onClick={() => setRedactMode('blackout')}
                style={{
                  padding: '7px 16px',
                  borderRadius: 20,
                  border:
                    redactMode === 'blackout'
                      ? '2px solid #3498db'
                      : '1px solid #555',
                  background: '#000',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                }}
              >
                Blackout
              </button>
              <button
                onClick={() => setRedactMode('blur')}
                style={{
                  padding: '7px 16px',
                  borderRadius: 20,
                  border:
                    redactMode === 'blur'
                      ? '2px solid #3498db'
                      : '1px solid #555',
                  background: '#555',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                }}
              >
                Blur
              </button>
              <button
                onClick={() => setRects((prev) => prev.slice(0, -1))}
                style={{
                  padding: '7px 16px',
                  borderRadius: 20,
                  border: '1px solid #c0392b',
                  background: 'none',
                  color: '#e74c3c',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                }}
              >
                Undo
              </button>
              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                Draw on image to redact
              </span>
            </>
          )}
          {activeTab === 'adjust' && (
            <div
              style={{ display: 'flex', gap: 30, width: '80%', maxWidth: 480 }}
            >
              {[
                ['Brightness', brightness, setBrightness],
                ['Contrast', contrast, setContrast],
              ].map(([label, val, setter]) => (
                <div key={label} style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: '#aaa',
                      marginBottom: 4,
                    }}
                  >
                    <span>{label}</span>
                    <span>{val}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={val}
                    onChange={(e) => setter(e.target.value)}
                    style={{ width: '100%', accentColor: '#3498db' }}
                  />
                </div>
              ))}
            </div>
          )}
          {activeTab === 'rotate' && (
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setRotation((r) => r - 90)}
                style={rotBtn}
              >
                &#8634; Left 90°
              </button>
              <button
                onClick={() => setRotation((r) => r + 90)}
                style={rotBtn}
              >
                Right 90° &#8635;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const overlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const editorBox = {
  background: '#1e1e2e',
  padding: 30,
  borderRadius: 12,
  maxWidth: '90%',
  textAlign: 'center',
  color: 'white',
};
const rotBtn = {
  padding: '8px 16px',
  borderRadius: 6,
  background: '#2c3e50',
  border: '1px solid #34495e',
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.83rem',
};

// ─── Scan Card ─────────────────────────────────────────────────────────────────

const ScanCard = ({ scan, userProfile, isOwner = false }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [commentStatus, setCommentStatus] = useState('');
  const [resolveNote, setResolveNote] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  const [resolveStatus, setResolveStatus] = useState('');

  useEffect(() => {
    if (!showComments) return;
    api
      .getComments(scan.id)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setComments(
          [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
      })
      .catch(console.error);
  }, [showComments, scan.id]);

  const postComment = async () => {
    if (!commentText.trim()) return;
    setCommentStatus('Posting...');
    try {
      await api.addComment(scan.id, {
        content: commentText,
        annotationData: '',
        parentId: null,
        isSuggestedImpression: false,
      });
      const fresh = await api.getComments(scan.id);
      const list = Array.isArray(fresh) ? fresh : [];
      setComments(
        [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
      setCommentText('');
      setCommentStatus('');
    } catch (err) {
      setCommentStatus('Failed to post: ' + err.message);
    }
  };

  const handleResolve = async () => {
    setResolveStatus('Resolving...');
    try {
      await api.resolveCase(scan.id, resolveNote);
      setResolveStatus('Marked as resolved ✓');
      setShowResolve(false);
    } catch (e) {
      setResolveStatus('Error: ' + e.message);
    }
  };

  const st = scan.status || 'PENDING_REVIEW';

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Image */}
      <div
        style={{
          position: 'relative',
          height: 160,
          background: '#1a1a2e',
          overflow: 'hidden',
        }}
      >
        {scan.imageUrl ? (
          <img
            src={scan.imageUrl}
            alt="Scan"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.9,
            }}
          />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#3498db',
            }}
          >
            <span style={{ fontSize: '2.5rem' }}>🔬</span>
            <span
              style={{ fontSize: '0.75rem', color: '#666', marginTop: 6 }}
            >
              {scan.imageName || 'No preview'}
            </span>
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: statusColor[st] || '#95a5a6',
            color: '#fff',
            padding: '3px 10px',
            borderRadius: 20,
            fontSize: '0.68rem',
            fontWeight: 600,
          }}
        >
          {statusLabel[st] || st}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '14px 16px 12px', flex: 1 }}>
        <h4
          style={{
            margin: '0 0 4px',
            fontSize: '0.92rem',
            color: '#2c3e50',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {scan.title || scan.reportPatientName || 'Untitled Case'}
        </h4>
        <div
          style={{
            fontSize: '0.72rem',
            color: '#888',
            marginBottom: 8,
          }}
        >
          {scan.ownerName && <span>👤 {scan.ownerName}</span>}
          {scan.ownerDepartment && (
            <span style={{ marginLeft: 8 }}>🏥 {scan.ownerDepartment}</span>
          )}
        </div>
        {scan.requestMessage && (
          <p
            style={{
              fontSize: '0.76rem',
              color: '#555',
              margin: '0 0 8px',
              lineHeight: 1.5,
              borderLeft: '3px solid #3498db',
              paddingLeft: 8,
            }}
          >
            {scan.requestMessage}
          </p>
        )}
        <div
          style={{
            display: 'flex',
            gap: 12,
            fontSize: '0.7rem',
            color: '#999',
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <span>💬 {scan.commentCount ?? 0} comments</span>
          <span>👁 {scan.accessCount ?? 0} views</span>
          <span>📅 {formatDate(scan.createdAt)}</span>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowComments((s) => !s)}
            style={actionBtn('#3498db')}
          >
            {showComments ? 'Hide' : '💬 Discuss'}
          </button>
          {isOwner && st !== 'RESOLVED' && (
            <button
              onClick={() => setShowResolve((s) => !s)}
              style={actionBtn('#27ae60')}
            >
              ✓ Resolve
            </button>
          )}
        </div>

        {showResolve && (
          <div
            style={{
              marginTop: 10,
              background: '#f0fff4',
              borderRadius: 8,
              padding: 10,
            }}
          >
            <textarea
              placeholder="Resolution notes (optional)..."
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              style={{
                width: '100%',
                minHeight: 60,
                padding: 8,
                borderRadius: 6,
                border: '1px solid #ddd',
                fontSize: '0.8rem',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button onClick={handleResolve} style={actionBtn('#27ae60')}>
                Confirm Resolve
              </button>
              <button
                onClick={() => setShowResolve(false)}
                style={actionBtn('#95a5a6')}
              >
                Cancel
              </button>
            </div>
            {resolveStatus && (
              <small
                style={{
                  color: resolveStatus.includes('Error') ? 'red' : 'green',
                }}
              >
                {resolveStatus}
              </small>
            )}
          </div>
        )}

        {showComments && (
          <div
            style={{
              marginTop: 12,
              borderTop: '1px solid #f0f0f0',
              paddingTop: 10,
            }}
          >
            <div
              style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 8 }}
            >
              {comments.length === 0 && (
                <p
                  style={{
                    fontSize: '0.76rem',
                    color: '#bbb',
                    textAlign: 'center',
                    padding: '10px 0',
                  }}
                >
                  No comments yet. Be first!
                </p>
              )}
              {comments.map((c) => (
                <div
                  key={c.id}
                  style={{
                    marginBottom: 10,
                    paddingBottom: 8,
                    borderBottom: '1px solid #f9f9f9',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 2,
                    }}
                  >
                    <strong
                      style={{ fontSize: '0.73rem', color: '#2c3e50' }}
                    >
                      {c.authorName}
                    </strong>
                    <span style={{ fontSize: '0.68rem', color: '#bbb' }}>
                      {formatDate(c.createdAt)}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '0.8rem',
                      color: '#444',
                      lineHeight: 1.4,
                    }}
                  >
                    {c.content}
                  </p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && postComment()}
                placeholder="Add professional comment..."
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #ddd',
                  fontSize: '0.8rem',
                  minWidth: 0,
                }}
              />
              <button
                onClick={postComment}
                style={{
                  background: '#3498db',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  flexShrink: 0,
                }}
              >
                Post
              </button>
            </div>
            {commentStatus && (
              <small style={{ color: '#888' }}>{commentStatus}</small>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const actionBtn = (color) => ({
  padding: '5px 12px',
  borderRadius: 6,
  border: 'none',
  background: color,
  color: '#fff',
  cursor: 'pointer',
  fontSize: '0.76rem',
  fontWeight: 500,
});

// ─── Upload Modal ─────────────────────────────────────────────────────────────

const UploadModal = ({ file, onClose, onSuccess, userProfile }) => {
  const [hasEdited, setHasEdited] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editedFile, setEditedFile] = useState(file);
  const [category, setCategory] = useState(SCAN_ENUMS[0].label);
  const [title, setTitle] = useState(
    file.name.replace(/\.[^/.]+$/, '')
  );
  const [message, setMessage] = useState('');
  const [sharingLevel, setSharingLevel] = useState('SPECIFIC_COLLEAGUES');
  const [colleagues, setColleagues] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [colleagueSearch, setColleagueSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .getColleagues()
      .then((users) => {
        const filtered = (users || []).filter(
          (u) => u.id !== userProfile?.id && u.id !== userProfile?.uid
        );
        setColleagues(filtered);
      })
      .catch((e) => console.warn('[UploadModal] getColleagues failed:', e));
  }, [userProfile]);

  const toggleColleague = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredColleagues = colleagues.filter((u) => {
    const q = colleagueSearch.toLowerCase();
    return (
      (u.firstName || '').toLowerCase().includes(q) ||
      (u.lastName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  });

  const handleSubmit = async () => {
    if (!hasEdited) {
      setStatus('⚠️ You must open the Editor and verify the image first.');
      return;
    }
    if (
      sharingLevel === 'SPECIFIC_COLLEAGUES' &&
      selectedIds.length === 0
    ) {
      setStatus('⚠️ Select at least one colleague to share with.');
      return;
    }

    setLoading(true);
    setStatus('Uploading...');

    try {
      const formData = new FormData();
      formData.append('image', editedFile);
      formData.append('title', title || editedFile.name);
      formData.append('requestMessage', `${category} — ${message}`);
      formData.append('sharingLevel', sharingLevel);

      if (sharingLevel === 'SPECIFIC_COLLEAGUES') {
        selectedIds.forEach((id) => formData.append('colleagueIds', id));
      }

      await api.shareCase(formData);
      setStatus('✅ Upload successful!');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1200);
    } catch (err) {
      setStatus('❌ ' + err.message);
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (showEditor)
    return (
      <MediaEditor
        file={editedFile}
        onSave={(f) => {
          setEditedFile(f);
          setHasEdited(true);
          setShowEditor(false);
        }}
        onCancel={() => setShowEditor(false)}
      />
    );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: 500,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            padding: '18px 20px 14px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.05rem' }}>
            📤 Share Case
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.4rem',
              color: '#999',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '16px 20px 20px' }}>
          <div
            style={{
              background: '#f8f9fa',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 14,
              fontSize: '0.82rem',
              color: '#555',
              wordBreak: 'break-all',
            }}
          >
            📎 <strong>{editedFile.name}</strong> ·{' '}
            {(editedFile.size / 1024).toFixed(1)} KB
          </div>

          <button
            onClick={() => setShowEditor(true)}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: 14,
              borderRadius: 8,
              border: 'none',
              background: hasEdited ? '#27ae60' : '#e67e22',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.88rem',
            }}
          >
            {hasEdited
              ? '✓ Anonymized & Verified — Click to Re-edit'
              : '🔏 Open Editor to Anonymize (Required)'}
          </button>

          <label style={labelStyle}>Case Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            placeholder="e.g. Right upper quadrant mass"
          />

          <label style={labelStyle}>Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={inputStyle}
          >
            {SCAN_ENUMS.map((c) => (
              <option key={c.value} value={c.label}>
                {c.label}
              </option>
            ))}
          </select>

          <label style={labelStyle}>Clinical Question / Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            placeholder="e.g. Query hepatic lesion — opinion on characterisation?"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />

          <label style={labelStyle}>Share With</label>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <button
              onClick={() => setSharingLevel('SPECIFIC_COLLEAGUES')}
              style={{
                flex: 1,
                padding: '8px 0',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: 500,
                background:
                  sharingLevel === 'SPECIFIC_COLLEAGUES'
                    ? '#3498db'
                    : '#f0f0f0',
                color:
                  sharingLevel === 'SPECIFIC_COLLEAGUES' ? '#fff' : '#555',
                border: 'none',
              }}
            >
              👥 Specific Colleagues
            </button>
          </div>

          {sharingLevel === 'SPECIFIC_COLLEAGUES' && (
            <div style={{ marginBottom: 14 }}>
              <input
                value={colleagueSearch}
                onChange={(e) => setColleagueSearch(e.target.value)}
                placeholder="Search colleagues..."
                style={{ ...inputStyle, marginBottom: 6 }}
              />
              <div
                style={{
                  maxHeight: 150,
                  overflowY: 'auto',
                  border: '1px solid #eee',
                  borderRadius: 8,
                }}
              >
                {filteredColleagues.length === 0 && (
                  <p
                    style={{
                      padding: 12,
                      color: '#bbb',
                      fontSize: '0.8rem',
                      textAlign: 'center',
                    }}
                  >
                    No colleagues found
                  </p>
                )}
                {filteredColleagues.map((u) => {
                  const id = u.id || u.uid;
                  const checked = selectedIds.includes(id);
                  return (
                    <div
                      key={id}
                      onClick={() => toggleColleague(id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        background: checked ? '#eaf4fd' : 'transparent',
                        borderBottom: '1px solid #f5f5f5',
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: '2px solid #3498db',
                          background: checked ? '#3498db' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {checked && (
                          <span
                            style={{
                              color: '#fff',
                              fontSize: 10,
                              lineHeight: 1,
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '0.82rem',
                            fontWeight: 500,
                            color: '#2c3e50',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {u.firstName || ''} {u.lastName || ''}{' '}
                          {!u.firstName && !u.lastName ? u.email : ''}
                        </div>
                        {(u.firstName || u.lastName) && (
                          <div
                            style={{ fontSize: '0.72rem', color: '#999' }}
                          >
                            {u.email}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selectedIds.length > 0 && (
                <p
                  style={{
                    fontSize: '0.75rem',
                    color: '#3498db',
                    margin: '4px 0 0',
                  }}
                >
                  {selectedIds.length} colleague
                  {selectedIds.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}

          {status && (
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                marginBottom: 12,
                fontSize: '0.82rem',
                background:
                  status.includes('❌') || status.includes('⚠️')
                    ? '#fff5f5'
                    : status.includes('✅')
                    ? '#f0fff4'
                    : '#f0f7ff',
                color:
                  status.includes('❌') || status.includes('⚠️')
                    ? '#c0392b'
                    : status.includes('✅')
                    ? '#27ae60'
                    : '#3498db',
                border: `1px solid ${
                  status.includes('❌') || status.includes('⚠️')
                    ? '#f5c6cb'
                    : status.includes('✅')
                    ? '#c3e6cb'
                    : '#bee3f8'
                }`,
              }}
            >
              {status}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '11px',
              fontSize: '0.9rem',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Uploading…' : '🚀 Submit Case'}
          </button>
        </div>
      </div>
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: '#555',
  marginBottom: 4,
};
const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #ddd',
  fontSize: '0.85rem',
  marginBottom: 12,
  boxSizing: 'border-box',
  outline: 'none',
};

// ─── Main SonoShare ───────────────────────────────────────────────────────────

const SonoShare = ({ userProfile, onBack }) => {
  const { isMobile } = useViewport();

  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('shared-with-me');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [myShares, setMyShares] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressStatus, setCompressStatus] = useState('');

  useEffect(() => {
    const workspace = document.querySelector('.workspace');
    if (workspace) workspace.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadFeeds = useCallback(async () => {
    setLoadingFeed(true);
    try {
      const [swm, ms] = await Promise.all([
        api.getSharedWithMe(),
        api.getMyShares(),
      ]);
      setSharedWithMe(Array.isArray(swm) ? swm : []);
      setMyShares(Array.isArray(ms) ? ms : []);
    } catch (e) {
      console.error('[SonoShare] Failed to load feeds:', e);
    } finally {
      setLoadingFeed(false);
    }
  }, []);

  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    const MAX = 1 * 1024 * 1024;
    if (file.size > MAX) {
      if (file.type.startsWith('video')) {
        alert(
          `Video too large (${(file.size / 1024 / 1024).toFixed(
            1
          )}MB). Max is 1MB. Please compress first.`
        );
        return;
      }
      setIsCompressing(true);
      setCompressStatus('Compressing image…');
      try {
        const compressed = await compressImage(file, 1);
        setFileToUpload(compressed);
      } catch {
        alert('Compression failed. Please try another file.');
      } finally {
        setIsCompressing(false);
        setCompressStatus('');
      }
    } else {
      setFileToUpload(file);
    }
  };

  const filterByCat = (list) => {
    if (selectedCategory === 'ALL') return list;
    return list.filter((s) => (s.requestMessage || '').includes(selectedCategory));
  };

  const feed =
    activeTab === 'shared-with-me'
      ? filterByCat(sharedWithMe)
      : filterByCat(myShares);
  const isOwnerTab = activeTab === 'my-shares';

  return (
    <div
      className="view-container fade-in"
      style={{
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        padding: isMobile ? '12px' : '20px',
      }}
    >
      {fileToUpload && (
        <UploadModal
          file={fileToUpload}
          userProfile={userProfile}
          onClose={() => setFileToUpload(null)}
          onSuccess={loadFeeds}
        />
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="video/*,image/*"
        style={{ display: 'none' }}
      />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <button
          onClick={() => {
            window.scrollTo(0, 0);
            if (onBack) onBack();
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#3498db',
            cursor: 'pointer',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 0,
          }}
        >
          ← Back
        </button>
        <div style={{ textAlign: 'center', flex: isMobile ? '1 1 100%' : 1 }}>
          <h3 style={{ margin: 0, color: '#2c3e50' }}>🔬 Sono Share</h3>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#999' }}>
            Peer review &amp; collaboration
          </p>
        </div>
        <button
          className="btn-primary"
          style={{
            padding: '9px 16px',
            fontSize: '0.85rem',
            opacity: isCompressing ? 0.6 : 1,
            width: isMobile ? '100%' : 'auto',
          }}
          disabled={isCompressing}
          onClick={() => fileInputRef.current.click()}
        >
          {isCompressing ? compressStatus : '+ Upload Case'}
        </button>
      </div>

      {/* Tabs + Filter */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid #f0f0f0',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', overflowX: 'auto', maxWidth: '100%' }}>
          {[
            [
              'shared-with-me',
              `📥 Shared With Me (${sharedWithMe.length})`,
            ],
            ['my-shares', `📤 My Shares (${myShares.length})`],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                padding: '10px 18px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: activeTab === id ? 700 : 400,
                color: activeTab === id ? '#3498db' : '#777',
                borderBottom:
                  activeTab === id
                    ? '2px solid #3498db'
                    : '2px solid transparent',
                marginBottom: -2,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            paddingBottom: 8,
          }}
        >
          <span
            style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b' }}
          >
            Filter:
          </span>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              fontSize: '0.85rem',
              outline: 'none',
              background: '#f8fafc',
              color: '#0f172a',
              maxWidth: 220,
            }}
          >
            <option value="ALL">All Categories</option>
            {SCAN_ENUMS.map((c) => (
              <option key={c.value} value={c.label}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Feed */}
      {loadingFeed ? (
        <div
          style={{ textAlign: 'center', padding: '50px 0', color: '#bbb' }}
        >
          <div style={{ fontSize: '2rem', marginBottom: 10 }}>⏳</div>
          <p>Loading cases…</p>
        </div>
      ) : feed.length === 0 ? (
        <div
          style={{ textAlign: 'center', padding: '50px 20px', color: '#bbb' }}
        >
          <div style={{ fontSize: '3rem', marginBottom: 10 }}>🔬</div>
          <p style={{ fontSize: '0.9rem' }}>
            No cases found
            {selectedCategory !== 'ALL' ? ` in ${selectedCategory}` : ''}.
          </p>
          {activeTab === 'my-shares' && (
            <button
              className="btn-primary"
              style={{ marginTop: 10 }}
              onClick={() => fileInputRef.current.click()}
            >
              Upload Your First Case
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? '1fr'
              : 'repeat(auto-fill, minmax(270px, 1fr))',
            gap: 16,
          }}
        >
          {feed.map((scan) => (
            <ScanCard
              key={scan.id}
              scan={scan}
              userProfile={userProfile}
              isOwner={isOwnerTab}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SonoShare;